import { z } from 'zod'

const emptyToUndefined = (value: unknown) => {
  if (value === null || value === undefined) return undefined
  if (typeof value === 'string' && value.trim() === '') return undefined
  return value
}

const emptyToNull = (value: unknown) => {
  if (value === undefined) return undefined
  if (value === null) return null
  if (typeof value === 'string' && value.trim() === '') return null
  return value
}

const optionalTrimmedString = (maxLength: number, message: string) =>
  z.preprocess(emptyToUndefined, z.string().trim().max(maxLength, message).optional())

const nullableTrimmedString = (maxLength: number, message: string) =>
  z.preprocess(emptyToNull, z.string().trim().max(maxLength, message).nullable().optional())

export const nameSchema = z
  .string()
  .min(2, 'Name must be at least 2 characters')
  .max(50, 'Name must be less than 50 characters')
  .regex(/^[a-zA-Z\s\-']+$/, 'Name contains invalid characters')

export const emailSchema = z
  .string()
  .trim()
  .min(1, 'Client email cannot be empty')
  .email('Client email must be a valid email address')
  .max(255, 'Email must be less than 255 characters')
  .transform((value) => value.toLowerCase())

export const phoneSchema = z
  .string()
  .regex(/^(\+27|0)[6-8][0-9]{8}$/, 'Invalid South African phone number')

export const ClientNameSchema = z
  .string({
    required_error: 'Missing required field: client_name',
    invalid_type_error: 'Missing required field: client_name',
  })
  .trim()
  .min(1, 'Client name must be between 1 and 200 characters')
  .max(200, 'Client name must be between 1 and 200 characters')

export const ClientCountryCodeSchema = z
  .string()
  .trim()
  .min(2, 'Client country must be a valid 2-3 character ISO code')
  .max(3, 'Client country must be a valid 2-3 character ISO code')

export const OptionalClientCountryCodeSchema = z.preprocess(
  emptyToNull,
  ClientCountryCodeSchema.nullable().optional()
)

export const ProjectCodeSchema = z
  .string()
  .trim()
  .regex(/^PRJ-\d{4}$/, 'Project code must follow PRJ-XXXX format (e.g., PRJ-1234)')

export const OptionalProjectCodeSchema = z.preprocess(
  emptyToUndefined,
  ProjectCodeSchema.optional()
)

export const ClientCreateSchema = z
  .object({
    client_name: ClientNameSchema,
    client_email: emailSchema.optional(),
    contact_email: emailSchema.optional(),
    client_country: OptionalClientCountryCodeSchema,
    registration_number: nullableTrimmedString(
      50,
      'Registration number must be less than 50 characters'
    ),
    default_project_code: OptionalProjectCodeSchema,
    contact_person: optionalTrimmedString(200, 'Contact person must be less than 200 characters'),
  })
  .passthrough()
  .superRefine((value, context) => {
    if (!value.client_email && !value.contact_email) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['client_email'],
        message: 'Missing required field: client_email',
      })
    }
  })
  .transform((value) => ({
    ...value,
    client_email: value.client_email ?? value.contact_email!,
  }))

export const ClientUpdateSchema = z
  .object({
    client_name: ClientNameSchema.optional(),
    client_email: emailSchema.optional(),
    client_country: OptionalClientCountryCodeSchema,
    registration_number: nullableTrimmedString(
      50,
      'Registration number must be less than 50 characters'
    ),
    default_project_code: ProjectCodeSchema.optional(),
    contact_person: optionalTrimmedString(200, 'Contact person must be less than 200 characters'),
  })
  .passthrough()

export const createClientSchema = ClientCreateSchema
export const updateClientSchema = ClientUpdateSchema

export function validateProjectCodeFormat(code: string): boolean {
  return ProjectCodeSchema.safeParse(code).success
}

export type CreateClientInput = z.infer<typeof ClientCreateSchema>
export type UpdateClientInput = z.infer<typeof ClientUpdateSchema>
